import React from 'react';
import { Controller } from 'react-hook-form';
import { Button, Checkbox, Form, Header, Icon, Message, Segment, Table } from 'semantic-ui-react';
import { PhoneUtil } from 'utils';
import TierLabel from '../subcomponents/TierLabel';
import { NO_ANSWER_OPTIONS, RECOMMENDATION_TIERS, RING_STRATEGY_OPTIONS } from '../constants';

const DIGIT_OPTIONS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'].map(digit => ({ key: digit, value: digit, text: digit }));

const GreetingScript = ({ control, name, label, onRedraft, isDraftingGreetings }) => (
    <Controller
        name={name}
        control={control}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
            <Form.Field error={!!error}>
                <div className='Onboarding__script-label'>
                    <label>{label}</label>
                    <Button basic size='mini' type='button' loading={isDraftingGreetings} disabled={isDraftingGreetings} onClick={onRedraft}>
                        <Icon name='magic' /> Redraft
                    </Button>
                </div>
                <Form.TextArea name={name} value={value} onChange={onChange} rows={3} />
                {error && <div className='Onboarding__field-error'>{error.message}</div>}
            </Form.Field>
        )}
    />
);

const RoutingAndVoicemail = ({
    control,
    teamOptions,
    isIvrEnabled,
    ivrOptionFields,
    duplicateDigits,
    explanations,
    isDraftingGreetings,
    onRedraftGreetings,
    onBack,
    onContinue
}) => (
    <Segment>
        <Header as='h3'>
            Call Routing & Voicemail <TierLabel tier={RECOMMENDATION_TIERS.REQUIRES_ACTIVATION} />
            <Header.Subheader>Routing becomes active once your number is set up and verified.</Header.Subheader>
        </Header>
        <Form noValidate>
            <Header as='h4'>
                During Business Hours
                <Header.Subheader>{explanations.routing}</Header.Subheader>
            </Header>
            <Controller
                name='ivr.enabled'
                control={control}
                render={({ field: { name, value, onChange } }) => (
                    <Form.Field>
                        <Checkbox
                            toggle
                            checked={!!value}
                            label='Greet callers with a menu (IVR), e.g. "Press 1 for Sales"'
                            onChange={(_, { checked }) => onChange({ target: { name, value: checked } })}
                        />
                    </Form.Field>
                )}
            />
            {isIvrEnabled ? (
                <>
                    <Table compact unstackable className='Onboarding__ivr-options'>
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell width={3}>Key</Table.HeaderCell>
                                <Table.HeaderCell>Rings Team</Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {ivrOptionFields.map((option, index) => (
                                <Table.Row key={option.id}>
                                    <Table.Cell>
                                        <Controller
                                            name={`ivr.options.${index}.digit`}
                                            control={control}
                                            render={({ field: { name, value, onChange } }) => (
                                                <Form.Select
                                                    compact
                                                    name={name}
                                                    value={value}
                                                    options={DIGIT_OPTIONS}
                                                    error={duplicateDigits.includes(index)}
                                                    onChange={(_, { value }) => onChange({ target: { name, value } })}
                                                />
                                            )}
                                        />
                                    </Table.Cell>
                                    <Table.Cell>{teamOptions.find(({ value }) => value === option.team_id)?.text}</Table.Cell>
                                </Table.Row>
                            ))}
                        </Table.Body>
                    </Table>
                    {duplicateDigits.length > 0 && <Message warning size='small'>Each menu option needs a different key.</Message>}
                    <GreetingScript
                        control={control}
                        name='ivr.greeting_script'
                        label='Menu Greeting Script'
                        onRedraft={onRedraftGreetings}
                        isDraftingGreetings={isDraftingGreetings}
                    />
                </>
            ) : (
                <Controller
                    name='routing.team_id'
                    control={control}
                    render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                        <Form.Select
                            width={8}
                            label='Ring This Team'
                            required
                            name={name}
                            value={value}
                            options={teamOptions}
                            onChange={(_, { value }) => onChange({ target: { name, value } })}
                            error={error?.message}
                        />
                    )}
                />
            )}
            <Form.Group widths='equal'>
                <Controller
                    name='routing.ring_strategy'
                    control={control}
                    render={({ field: { name, value, onChange } }) => (
                        <Form.Select
                            label='How Team Phones Ring'
                            name={name}
                            value={value}
                            options={RING_STRATEGY_OPTIONS}
                            onChange={(_, { value }) => onChange({ target: { name, value } })}
                        />
                    )}
                />
                <Controller
                    name='routing.ring_time'
                    control={control}
                    render={({ field: { name, value, onChange } }) => (
                        <Form.Select
                            label='Ring For'
                            name={name}
                            value={value}
                            options={PhoneUtil.RING_DURATION_OPTIONS}
                            onChange={(_, { value }) => onChange({ target: { name, value } })}
                        />
                    )}
                />
                <Controller
                    name='routing.no_answer_destination'
                    control={control}
                    render={({ field: { name, value, onChange } }) => (
                        <Form.Select
                            label='If Nobody Answers'
                            name={name}
                            value={value}
                            options={NO_ANSWER_OPTIONS}
                            onChange={(_, { value }) => onChange({ target: { name, value } })}
                        />
                    )}
                />
            </Form.Group>
            <p className='Onboarding__hint'>{explanations.ring_strategy}</p>

            <Header as='h4'>
                After Hours & Voicemail
                <Header.Subheader>After hours, calls go to your company voicemail.</Header.Subheader>
            </Header>
            <GreetingScript
                control={control}
                name='voicemail.greeting_script'
                label='Voicemail Greeting Script'
                onRedraft={onRedraftGreetings}
                isDraftingGreetings={isDraftingGreetings}
            />
            <Message info size='small'>
                <Icon name='microphone' />
                These are scripts to read from. After launch, record or upload each greeting in Settings → Phone Management → Audio.
            </Message>
        </Form>
        <div className='Onboarding__footer'>
            <Button basic type='button' onClick={onBack}><Icon name='arrow left' /> Back</Button>
            <Button primary floated='right' type='button' onClick={onContinue}>
                Review Phone Setup <Icon name='arrow right' />
            </Button>
            <div style={{ clear: 'both' }} />
        </div>
    </Segment>
);

export default RoutingAndVoicemail;
